import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminContext } from '../context/AdminContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CategoryHero from '../components/CategoryHero';
import MedicineRow from '../components/MedicineRow';
import ConsultationBanner from '../components/ConsultationBanner';
import Testimonials from '../components/Testimonials';
import FAQ from '../components/FAQ';
import { Search, Filter, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';

const CategoryPage = () => {
    const { id } = useParams();
    const { siteData } = useAdminContext();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSubCategory, setActiveSubCategory] = useState('All');
    const [visibleCount, setVisibleCount] = useState(10);

    const category = siteData.categories.list.find(c => c.id === id);

    useEffect(() => {
        window.scrollTo(0, 0);
        setVisibleCount(10); // Reset count on category change
    }, [id]);

    if (!category) {
        return (
            <div className="min-h-screen flex items-center justify-center font-sans">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4">Category not found</h2>
                    <button onClick={() => navigate('/')} className="text-red-600 font-bold underline">Go Back Home</button>
                </div>
            </div>
        );
    }

    const filteredMedicines = (category.medicines || []).filter(med => {
        const matchesSearch = med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (med.indication || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = activeSubCategory === 'All' || med.category === activeSubCategory;
        return matchesSearch && matchesCategory;
    });

    const displayedMedicines = filteredMedicines.slice(0, visibleCount);

    const handleWhatsAppClick = (med = null) => {
        const message = med
            ? `Hi, I'm interested in ${med.name} (${med.strength}) for ${category.name}. Please provide pricing and availability.`
            : `Hi, I'm looking for medications in the ${category.name} category. Can you help?`;
        window.open(`https://wa.me/${siteData.contact.whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
    };

    const handleConsultationClick = () => {
        navigate('/#consultation');
        setTimeout(() => {
            document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans">
            <Header />

            <CategoryHero category={category} />

            <section className="py-8 bg-white border-b border-gray-100 sticky top-[72px] z-30 shadow-sm">
                <div className="container mx-auto px-4 max-w-6xl">
                    <div className="flex flex-col md:flex-row gap-4 items-center">
                        {/* Search */}
                        <div className="relative flex-1 w-full">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={`Search ${category.name} medications...`}
                                className="pl-12 h-14 rounded-2xl border-gray-200 focus:ring-2 focus:ring-red-500/20 focus:border-red-500 shadow-sm transition-all text-base font-medium"
                            />
                        </div>

                        {/* Filters */}
                        <div className="flex flex-wrap gap-2 justify-center w-full md:w-auto">
                            {(category.subCategories || ['All']).map(sub => (
                                <button
                                    key={sub}
                                    onClick={() => setActiveSubCategory(sub)}
                                    className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all border-2 tracking-wider uppercase ${activeSubCategory === sub
                                        ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-100 scale-105'
                                        : 'bg-white border-gray-100 text-gray-500 hover:border-red-200 hover:text-red-600'
                                        }`}
                                >
                                    {sub}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-12 min-h-[400px]">
                <div className="container mx-auto px-4 max-w-6xl">
                    <p className="text-gray-400 text-xs font-bold mb-6 uppercase tracking-widest px-4">
                        Showing {displayedMedicines.length} of {filteredMedicines.length} medications
                    </p>

                    {displayedMedicines.length > 0 ? (
                        <div className="space-y-4">
                            <div className="bg-white rounded-[2.5rem] overflow-hidden shadow-xl shadow-slate-200/50 border border-slate-100 divide-y divide-slate-50">
                                <div className="bg-slate-50/80 py-4 px-10 hidden md:flex items-center justify-between text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                                    <span className="flex-1">Medication Details</span>
                                    <span className="min-w-[150px] text-right mr-32">US Cost vs Our Price</span>
                                    <span className="w-auto">Acquisition</span>
                                </div>
                                {displayedMedicines.map(med => (
                                    <MedicineRow
                                        key={med.id}
                                        medicine={med}
                                        onWhatsAppClick={handleWhatsAppClick}
                                    />
                                ))}
                            </div>

                            {/* Load More Button */}
                            {visibleCount < filteredMedicines.length && (
                                <div className="flex justify-center pt-8">
                                    <button
                                        onClick={() => setVisibleCount(prev => prev + 10)}
                                        className="px-10 py-4 bg-white border-2 border-red-100 text-red-600 font-black rounded-2xl hover:bg-red-50 hover:border-red-600 transition-all shadow-lg active:scale-95 text-sm uppercase tracking-widest"
                                    >
                                        Load More Medications
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-white rounded-[3rem] border-2 border-dashed border-slate-200 shadow-inner">
                            <div className="bg-slate-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                                <Search className="w-10 h-10 text-slate-300" />
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 mb-2">No medications found</h3>
                            <p className="text-slate-500 mb-8 max-w-sm mx-auto font-medium">We couldn't find any products matching "{searchTerm}" in this category.</p>
                            <button
                                onClick={() => { setSearchTerm(''); setActiveSubCategory('All'); }}
                                className="bg-red-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-red-200 hover:bg-red-700 transition-all"
                            >
                                Clear all filters
                            </button>
                        </div>
                    )}

                    {/* Category Specific Support Sections */}
                    {category.support && (
                        <div className="mt-20 space-y-20">
                            {/* CTA Card */}
                            <div className="bg-[#101828] rounded-[2.5rem] p-12 text-center text-white relative overflow-hidden shadow-2xl">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 blur-[100px] rounded-full" />
                                <h3 className="text-3xl font-black mb-4 relative z-10 italic">{category.support.ctaTitle}</h3>
                                <p className="text-slate-400 max-w-2xl mx-auto mb-8 relative z-10 text-lg">
                                    {category.support.ctaSubtitle}
                                </p>
                                <div className="flex flex-col md:flex-row justify-center gap-4 relative z-10">
                                    <button
                                        onClick={() => handleWhatsAppClick()}
                                        className="bg-[#25D366] hover:bg-[#1fb355] text-white px-8 py-4 rounded-xl font-black flex items-center justify-center shadow-lg transition-all active:scale-95"
                                    >
                                        <MessageCircle className="w-5 h-5 mr-2" />
                                        Chat on WhatsApp
                                    </button>
                                    <button
                                        onClick={handleConsultationClick}
                                        className="bg-white text-slate-900 hover:bg-slate-50 px-8 py-4 rounded-xl font-black shadow-lg transition-all active:scale-95"
                                    >
                                        Submit a Consultation
                                    </button>
                                </div>
                            </div>

                            {/* Testimonials */}
                            {category.support.testimonials && (
                                <div>
                                    <div className="text-center mb-12">
                                        <h3 className="text-3xl font-black italic mb-2">Patient Success Stories</h3>
                                        <p className="text-slate-500 font-medium">Real testimonials from patients receiving {category.name}</p>
                                    </div>
                                    <div className="grid md:grid-cols-3 gap-8">
                                        {category.support.testimonials.map(t => (
                                            <div key={t.id} className="bg-white p-8 rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-50 flex flex-col justify-between">
                                                <div>
                                                    <div className="flex justify-between items-start mb-6">
                                                        <div>
                                                            <h4 className="font-black text-lg">{t.name}</h4>
                                                            <p className="text-xs font-bold text-red-600">{t.condition}</p>
                                                        </div>
                                                        <div className="bg-green-50 text-green-700 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border border-green-100">
                                                            Save {t.savings}
                                                        </div>
                                                    </div>
                                                    <p className="text-slate-600 font-medium italic leading-relaxed">"{t.text}"</p>
                                                </div>
                                                <div className="mt-8 flex gap-1">
                                                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* FAQs */}
                            {category.support.faq && (
                                <FAQ
                                    faqs={category.support.faq}
                                    WhatsAppClick={() => handleWhatsAppClick()}
                                />
                            )}
                        </div>
                    )}
                </div>
            </section>

            <Footer />

            {/* Floating CTA */}
            <button
                onClick={() => handleWhatsAppClick()}
                className="fixed bottom-8 right-8 bg-[#25D366] hover:bg-[#1fb355] text-white p-5 rounded-full shadow-2xl hover:scale-110 transition-all z-50 flex items-center space-x-2 group"
            >
                <MessageCircle className="w-7 h-7" />
                <span className="hidden group-hover:inline-block pr-3 font-bold text-lg">Inquire Now</span>
            </button>
        </div>
    );
};

export default CategoryPage;
